import React, { useState, useEffect } from 'react';
import PagamentoModal from '../components/PagamentoModal';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Search, 
  ChevronDown, 
  Filter, 
  RefreshCcw, 
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Calendar,
  CreditCard,
  Wallet,
  Banknote
} from 'lucide-react';
import { firebaseService } from '../services/firebaseService';
import { Pagamento } from '../types/Pagamento';
import { useAuth } from '../contexts/AuthContext';
import { User } from '../types/User';

const Pagamentos: React.FC = () => {
  const { userProfile } = useAuth();
  const [pagamentos, setPagamentos] = useState<Pagamento[]>([]);
  const [filteredPagamentos, setFilteredPagamentos] = useState<Pagamento[]>([]);
  const [alunos, setAlunos] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => {
    return localStorage.getItem('pagamentos_searchTerm') || '';
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    return localStorage.getItem('pagamentos_statusFilter') || 'all';
  });
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('pagamentos_sortBy') || 'dataVencimento';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('pagamentos_sortOrder') || 'asc';
  });
  const [selectedPagamento, setSelectedPagamento] = useState<{id: string, alunoId: string, periodo: string} | null>(null);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showMonthFilter, setShowMonthFilter] = useState(false);
  const [monthFilter, setMonthFilter] = useState(() => {
    return localStorage.getItem('pagamentos_monthFilter') || 'all';
  });

  useEffect(() => {
    const fetchData = async () => {
      if (userProfile?.empresaCnpj) {
        try {
          const [pagamentosData, alunosData] = await Promise.all([
            firebaseService.getPagamentos(userProfile.empresaCnpj),
            firebaseService.getAlunos(userProfile.empresaCnpj)
          ]);
          
          setPagamentos(pagamentosData);
          setFilteredPagamentos(pagamentosData);
          setAlunos(alunosData);
          setLoading(false);
        } catch (error) {
          console.error('❌ Erro ao buscar dados:', error);
          setLoading(false);
          setPagamentos([]);
          setFilteredPagamentos([]);
          setAlunos([]);
        }
        } else {
        setLoading(false);
        setPagamentos([]);
        setFilteredPagamentos([]);
        setAlunos([]);
      }
    };

    fetchData();
  }, [userProfile]);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showStatusFilter && !target.closest('.status-filter')) {
        setShowStatusFilter(false);
      }
      if (showMonthFilter && !target.closest('.month-filter')) {
        setShowMonthFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusFilter, showMonthFilter]);

  useEffect(() => {
    let result = [...pagamentos];
    
    // Aplicar filtro de status
    if (statusFilter !== 'all') {
      result = result.filter(pagamento => pagamento.status === statusFilter);
    }
    
    // Aplicar filtro por mês
    if (monthFilter !== 'all') {
      result = result.filter(pagamento => {
        const dataVencimento = new Date(pagamento.dataVencimento);
        const mesAno = `${dataVencimento.getFullYear()}-${String(dataVencimento.getMonth() + 1).padStart(2, '0')}`;
        return mesAno === monthFilter;
      });
    }
    
    // Aplicar busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(pagamento => {
        const aluno = alunos.find(a => a.id === pagamento.alunoId);
        return (
          (aluno && aluno.nome?.toLowerCase().includes(term)) ||
          pagamento.codigo.toLowerCase().includes(term) ||
          pagamento.id.toLowerCase().includes(term)
        );
      });
    }
    
    // Aplicar ordenação
    result.sort((a, b) => {
      let valueA: any = a[sortBy as keyof Pagamento];
      let valueB: any = b[sortBy as keyof Pagamento];
      
      // Converter datas para objetos Date para comparação correta
      if (sortBy === 'dataVencimento' || sortBy === 'dataPagamento') {
        valueA = valueA ? new Date(valueA) : new Date(0);
        valueB = valueB ? new Date(valueB) : new Date(0);
      }
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }
      
      if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredPagamentos(result);
  }, [pagamentos, alunos, searchTerm, statusFilter, monthFilter, sortBy, sortOrder]);

  // Salvar filtros no localStorage
  useEffect(() => {
    localStorage.setItem('pagamentos_searchTerm', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem('pagamentos_statusFilter', statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    localStorage.setItem('pagamentos_sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('pagamentos_sortOrder', sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    localStorage.setItem('pagamentos_monthFilter', monthFilter);
  }, [monthFilter]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pago':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800"><CheckCircle className="h-3 w-3" /> Pago</span>;
      case 'cancelado':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800"><XCircle className="h-3 w-3" /> Cancelado</span>;
      case 'vencido':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800"><XCircle className="h-3 w-3" /> Vencido</span>;
      case 'pendente':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3" /> Pendente</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getPaymentTypeBadge = (type: string) => {
    switch (type) {
      case 'CREDIT_CARD':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"><CreditCard className="h-3 w-3" /> Cartão</span>;
      case 'BOLETO':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800"><Banknote className="h-3 w-3" /> Boleto</span>;
      case 'PIX':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800"><Wallet className="h-3 w-3" /> PIX</span>;
      case 'Dinheiro':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800"><Banknote className="h-3 w-3" /> Dinheiro</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{type}</span>;
    }
  };

  const formatCurrency = (value: number) => {
    if (value === null || value === undefined || isNaN(value)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    // Verificar se é formato ISO ou DD/MM/YYYY
    let date;
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      const [day, month, year] = dateString.split('/');
      date = new Date(`${year}-${month}-${day}`);
    }
    
    return date.toLocaleDateString('pt-BR');
  };

  const getAlunoNome = (alunoId: string) => {
    const aluno = alunos.find(a => a.id === alunoId);
    return aluno ? aluno.nome : 'Aluno não encontrado';
  };

  // Gerar lista de meses disponíveis
  const getMesesDisponiveis = () => {
    const meses = new Set<string>();
    pagamentos.forEach(pagamento => {
      const dataVencimento = new Date(pagamento.dataVencimento);
      const mesAno = `${dataVencimento.getFullYear()}-${String(dataVencimento.getMonth() + 1).padStart(2, '0')}`;
      meses.add(mesAno);
    });
    
    return Array.from(meses).sort().reverse(); // Mais recentes primeiro
  };

  const formatMonthDisplay = (mesAno: string) => {
    const [ano, mes] = mesAno.split('-');
    const data = new Date(parseInt(ano), parseInt(mes) - 1, 1);
    return data.toLocaleDateString('pt-BR', { year: 'numeric', month: 'long' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pagamentos</h1>
          <p className="text-gray-600">Gerencie os pagamentos dos alunos</p>
        </div>
        <div className="flex items-center gap-x-4">
          <button 
            className="flex items-center gap-x-2 px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setMonthFilter('all');
            }}
          >
            <RefreshCcw className="h-4 w-4" />
            Limpar Filtros
          </button>
          <button className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors">Novo Pagamento</button>
        </div>
      </div>

      {/* Resumo Financeiro */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4 bg-white dark:bg-gray-800 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Recebido</p>
              <p className="text-xl font-bold text-green-600">
                {formatCurrency(
                  filteredPagamentos
                    .filter(p => p.status === 'pago')
                    .reduce((sum, p) => sum + (p.valorPago || p.valor), 0)
                )}
              </p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-4 bg-white dark:bg-gray-800 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500">Pendente</p>
              <p className="text-xl font-bold text-yellow-600">
                {formatCurrency(
                  filteredPagamentos
                    .filter(p => p.status === 'pendente')
                    .reduce((sum, p) => sum + p.valor, 0)
                )}
              </p>
            </div>
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-4 bg-white dark:bg-gray-800 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500">Vencido</p>
              <p className="text-xl font-bold text-red-600">
                {formatCurrency(
                  filteredPagamentos
                    .filter(p => p.status === 'vencido')
                    .reduce((sum, p) => sum + p.valor, 0)
                )}
              </p>
            </div>
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="h-5 w-5 text-red-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-4 bg-white dark:bg-gray-800 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500">Total de Pagamentos</p>
              <p className="text-xl font-bold text-gray-900">{filteredPagamentos.length}</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Resultados e Filtros */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {loading ? 'Carregando...' : `${filteredPagamentos.length} pagamento${filteredPagamentos.length !== 1 ? 's' : ''} encontrado${filteredPagamentos.length !== 1 ? 's' : ''}`}
          {statusFilter !== 'all' && (
            <span className="ml-2 text-primary-600">
              (status: {statusFilter})
            </span>
          )}
          {monthFilter !== 'all' && (
            <span className="ml-2 text-primary-600">
              (mês: {formatMonthDisplay(monthFilter)})
            </span>
          )}
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-primary-500 focus:border-primary-500"
            placeholder="Buscar pagamento por aluno ou código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-4">
          <div className="relative status-filter">
            <button 
              className={`flex items-center gap-x-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${
                statusFilter !== 'all' 
                  ? 'border-primary-300 bg-primary-50 text-primary-700' 
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setShowStatusFilter(!showStatusFilter);
              }}
            >
              <Filter className="h-4 w-4" />
              Status {statusFilter !== 'all' && `(${statusFilter})`}
              <ChevronDown className="h-4 w-4" />
            </button>
            {showStatusFilter && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black dark:ring-gray-700 ring-opacity-5 z-10">
                <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setShowStatusFilter(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    Todos
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('pago');
                      setShowStatusFilter(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    Pagos
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('pendente');
                      setShowStatusFilter(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    Pendentes
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('vencido');
                      setShowStatusFilter(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    Vencidos
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="relative month-filter">
            <button 
              className={`flex items-center gap-x-2 px-4 py-2 rounded-md transition-colors ${
                monthFilter !== 'all' 
                  ? 'bg-primary-50 text-primary-700 hover:bg-primary-100' 
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setShowMonthFilter(!showMonthFilter);
              }}
            >
              <Calendar className="h-4 w-4" />
              Mês {monthFilter !== 'all' && `(${formatMonthDisplay(monthFilter)})`}
              <ChevronDown className="h-4 w-4" />
            </button>
            {showMonthFilter && (
              <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                  <button
                    onClick={() => {
                      setMonthFilter('all');
                      setShowMonthFilter(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    Todos os meses
                  </button>
                  {getMesesDisponiveis().map((mesAno) => (
                    <button
                      key={mesAno}
                      onClick={() => {
                        setMonthFilter(mesAno);
                        setShowMonthFilter(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                    >
                      {formatMonthDisplay(mesAno)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabela de Pagamentos */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse table-auto">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button 
                  className="flex items-center gap-x-1"
                  onClick={() => handleSort('alunoId')}
                >
                  Aluno
                  {sortBy === 'alunoId' && (
                    <ChevronDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button 
                  className="flex items-center gap-x-1"
                  onClick={() => handleSort('valor')}
                >
                  Valor
                  {sortBy === 'valor' && (
                    <ChevronDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button 
                  className="flex items-center gap-x-1"
                  onClick={() => handleSort('status')}
                >
                  Status
                  {sortBy === 'status' && (
                    <ChevronDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button 
                  className="flex items-center gap-x-1"
                  onClick={() => handleSort('tipoPagamento')}
                >
                  Forma de Pagamento
                  {sortBy === 'tipoPagamento' && (
                    <ChevronDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button 
                  className="flex items-center gap-x-1"
                  onClick={() => handleSort('dataVencimento')}
                >
                  Vencimento
                  {sortBy === 'dataVencimento' && (
                    <ChevronDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button 
                  className="flex items-center gap-x-1"
                  onClick={() => handleSort('dataPagamento')}
                >
                  Data Pagamento
                  {sortBy === 'dataPagamento' && (
                    <ChevronDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredPagamentos.length > 0 ? (
              filteredPagamentos.map((pagamento) => (
                <tr key={pagamento.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{getAlunoNome(pagamento.alunoId)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(pagamento.valor || 0)}
                    </div>
                    {pagamento.valorPago && pagamento.valorPago !== pagamento.valor ? (
                      <div className="text-xs text-gray-500">
                        Pago: {formatCurrency(pagamento.valorPago || 0)}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(pagamento.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getPaymentTypeBadge(pagamento.tipoPagamento)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(pagamento.dataVencimento)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {pagamento.dataPagamento ? formatDate(pagamento.dataPagamento) : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-x-2">
                      <button 
                        className="text-primary-600 hover:text-primary-900"
                        onClick={() => {
                          // Extrair o período (YYYY-MM) da data de vencimento
                          const dataVencimento = new Date(pagamento.dataVencimento);
                          const periodo = `${dataVencimento.getFullYear()}-${String(dataVencimento.getMonth() + 1).padStart(2, '0')}`;
                          
                          setSelectedPagamento({
                            id: pagamento.id,
                            alunoId: pagamento.alunoId,
                            periodo
                          });
                        }}
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button className="text-primary-600 hover:text-primary-900">
                        <Edit className="h-5 w-5" />
                      </button>
                      <div className="relative">
                        <button className="text-gray-400 hover:text-gray-600">
                          <MoreHorizontal className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  Nenhum pagamento encontrado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de Detalhes do Pagamento */}
      {selectedPagamento && (
        <PagamentoModal
          pagamentoId={selectedPagamento.id}
          alunoId={selectedPagamento.alunoId}
          periodo={selectedPagamento.periodo}
          onClose={() => setSelectedPagamento(null)}
        />
      )}

      {/* Paginação */}
      {filteredPagamentos.length > 0 && (
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6">
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando <span className="font-medium">1</span> a <span className="font-medium">{filteredPagamentos.length}</span> de <span className="font-medium">{filteredPagamentos.length}</span> resultados
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0">
                  Anterior
                </button>
                <button className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-primary-600 focus:z-20">
                  1
                </button>
                <button className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0">
                  Próximo
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pagamentos;
